/**
 * `/inventory/garments` — the finished-garment shelf tab (finished-garment-stock v1 PR-2).
 *
 * Pressed, ready-to-ship shirts on the shelf, grouped by DESIGN SET (the placements half of the
 * canonical identity) with one row per blank variant (style·colour·size) inside. On-hand is the
 * derived running sum of the garment-stock ledger — never a hand-kept counter. New stock enters via
 * INTAKE only (the /scan station's "Add to shelf", or the manual Add-shirts dialog here, which runs
 * the SAME catalog resolution order-matching uses); COUNT/ADJUST here correct existing rows.
 *
 * This tab is read/record only — nothing on it touches order lines or the blank ledger. The
 * "ship this order from the shelf" action is plan PR-4 (money path, review-gated).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, PackagePlus, Printer, Shirt } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { fetchAllDesigns } from '@/lib/api/designs';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { BinLabelSheet } from './bin-label-sheet';
import {
  type GarmentStockGroup,
  type GarmentStockRow,
  groupGarmentStock,
  type KindFilter,
  matchesKind,
  matchesStockSearch,
  rowBlankLabel,
} from './garment-stock-lib';
import { invalidateInventory } from './inventory-lib';
import { MovementList } from './movements-dialog';

type DesignSummary = components['schemas']['DesignSummary'];

type ProductSummary = components['schemas']['CatalogProductSummary'];

const KIND_LABEL: Record<KindFilter, string> = { all: 'All', tshirt: 'Tees', hoodie: 'Hoodies' };

export function GarmentStockPage() {
  const client = useApiClient();
  const { state } = useAuth();
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<KindFilter>('all');
  const [update, setUpdate] = useState<GarmentStockRow | null>(null);
  const [history, setHistory] = useState<GarmentStockRow | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // The bin-label print portal (bin-labels plan PR-2): one row, or every visible row (the
  // initial-walk "Print all"). Rendered OUTSIDE the dialogs so printing survives closing them.
  const [printJob, setPrintJob] = useState<{ rows: GarmentStockRow[]; title: string } | null>(null);

  const stock = useQuery({
    queryKey: ['garment-stock'],
    queryFn: async () => {
      const { data, error } = await client.GET('/garment-stock');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the garment shelf.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.items;
    },
  });
  // Design previews + style codes for the group headers. Cached app-wide under ['designs'].
  const designsQuery = useQuery({
    queryKey: ['designs'],
    staleTime: 5 * 60_000,
    queryFn: () => fetchAllDesigns(client),
  });
  const designById = useMemo(
    () => new Map((designsQuery.data ?? []).map((d) => [d.designId, d])),
    [designsQuery.data],
  );
  const styleCodes = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of designsQuery.data ?? []) if (d.styleCode) m.set(d.designId, d.styleCode);
    return m;
  }, [designsQuery.data]);

  const groups = useMemo(() => groupGarmentStock(stock.data ?? []), [stock.data]);
  const visible = groups.filter(
    (g) => matchesKind(g, kind) && matchesStockSearch(g, search, styleCodes),
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="max-w-2xl text-sm text-muted-foreground">
          Finished shirts on the shelf, ready to ship — by design, then size. Add stock by scanning
          a hem tag at the <span className="font-medium">Scan station</span>, or with{' '}
          <span className="font-medium">Add shirts</span> for untagged stock.
        </p>
        <Button
          size="sm"
          onClick={() => setAddOpen(true)}
          disabled={!canWrite}
          title={canWrite ? undefined : 'Admins only'}
        >
          <PackagePlus className="size-4" />
          Add shirts
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search designs, style codes, blanks…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 max-w-xs"
        />
        <ToggleGroup
          type="single"
          value={kind}
          onValueChange={(v) => setKind((v as KindFilter) || 'all')}
          variant="outline"
          size="sm"
        >
          {(['all', 'tshirt', 'hoodie'] as const).map((k) => (
            <ToggleGroupItem key={k} value={k} aria-label={KIND_LABEL[k]}>
              {KIND_LABEL[k]}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <Button
          variant="outline"
          size="sm"
          disabled={visible.length === 0}
          onClick={() =>
            setPrintJob({
              rows: visible.flatMap((g) => g.rows),
              title: search.trim() || kind !== 'all' ? 'filtered shelf' : 'the whole shelf',
            })
          }
          title="One bin label per size row currently shown"
        >
          <Printer className="size-4" />
          Print labels
        </Button>
      </div>

      {stock.isPending ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading the garment shelf…
        </div>
      ) : stock.isError ? (
        <p className="py-8 text-sm text-destructive">
          {stock.error instanceof Error ? stock.error.message : 'Failed to load.'}
        </p>
      ) : groups.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          Nothing on the garment shelf yet. Scan a finished shirt's hem tag at the Scan station, or
          use <span className="font-medium">Add shirts</span> to record untagged stock.
        </div>
      ) : visible.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">No shelf stock matches.</p>
      ) : (
        <div className="space-y-4">
          {visible.map((g) => (
            <StockGroup
              key={g.placementsKey}
              group={g}
              designById={designById}
              canWrite={canWrite}
              onUpdate={setUpdate}
              onHistory={setHistory}
              onPrint={(r) => setPrintJob({ rows: [r], title: rowBlankLabel(r) })}
            />
          ))}
        </div>
      )}

      <Dialog open={update !== null} onOpenChange={(o) => !o && setUpdate(null)}>
        <DialogContent className="max-h-[85vh] sm:max-w-md">
          {update && <UpdateStockBody key={update.stockKey} row={update} />}
        </DialogContent>
      </Dialog>

      <Dialog open={history !== null} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="max-h-[85vh] sm:max-w-lg">
          {history && <StockHistoryBody key={history.stockKey} row={history} />}
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={(o) => !o && setAddOpen(false)}>
        <DialogContent className="max-h-[85vh] sm:max-w-lg">
          {addOpen && (
            <AddShirtsBody
              onClose={() => setAddOpen(false)}
              onPrint={(r) => setPrintJob({ rows: [r], title: rowBlankLabel(r) })}
            />
          )}
        </DialogContent>
      </Dialog>

      {printJob && (
        <BinLabelSheet
          rows={printJob.rows}
          styleCodes={styleCodes}
          title={printJob.title}
          onClose={() => setPrintJob(null)}
        />
      )}
    </div>
  );
}

/** Small design thumbnail for a group header (preview WebP on a dark backing so transparent art
 *  reads; the designs page uses the same treatment). Placeholder icon when no READY preview. */
function GroupThumb({ design }: { design: DesignSummary | undefined }) {
  const url = design?.adult?.previewUrl ?? design?.youth?.previewUrl;
  if (!url) {
    return (
      <div className="flex size-10 flex-none items-center justify-center rounded-md border bg-muted text-muted-foreground">
        <Shirt className="size-5" />
      </div>
    );
  }
  return (
    <div className="size-10 flex-none overflow-hidden rounded-md border bg-zinc-900">
      <img src={url} alt="" loading="lazy" className="size-full object-contain" />
    </div>
  );
}

function StockGroup({
  group,
  designById,
  canWrite,
  onUpdate,
  onHistory,
  onPrint,
}: {
  group: GarmentStockGroup;
  designById: ReadonlyMap<string, DesignSummary>;
  canWrite: boolean;
  onUpdate: (r: GarmentStockRow) => void;
  onHistory: (r: GarmentStockRow) => void;
  onPrint: (r: GarmentStockRow) => void;
}) {
  // The back design's art is the group's face (the front is usually a shared brand logo).
  const face =
    group.placements.find((p) => p.location === 'back') ?? group.placements[0] ?? undefined;
  return (
    <section className="overflow-hidden rounded-md border">
      <header className="flex items-center gap-3 border-b bg-muted/40 px-3 py-2">
        <GroupThumb design={face ? designById.get(face.designId) : undefined} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{group.title}</div>
          <div className="text-xs text-muted-foreground">
            {group.placements.map((p) => p.location).join(' + ')}
          </div>
        </div>
        <div className="text-sm tabular-nums text-muted-foreground">
          {group.totalOnHand} on shelf
        </div>
      </header>
      <table className="w-full text-sm">
        <tbody>
          {group.rows.map((r) => (
            <tr key={r.stockKey} className="border-t first:border-t-0">
              <td className="px-3 py-2">{rowBlankLabel(r)}</td>
              <td className="w-20 px-3 py-2 text-right">
                <span
                  className={cn(
                    'tabular-nums',
                    r.onHand < 0 && 'font-semibold text-red-700 dark:text-red-400',
                  )}
                >
                  {r.onHand}
                </span>
              </td>
              <td className="w-48 px-3 py-2 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  className="mr-1 text-muted-foreground"
                  onClick={() => onPrint(r)}
                  title="Print this bin's label"
                  aria-label={`Print bin label for ${rowBlankLabel(r)}`}
                >
                  <Printer className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mr-1 text-muted-foreground"
                  onClick={() => onHistory(r)}
                >
                  History
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onUpdate(r)}
                  disabled={!canWrite}
                >
                  Update
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** COUNT / ADJUST for one existing row — the transfer tab's recorder, minus the picker (rows only;
 *  new identities enter through intake so a typo can never seed a phantom). */
function UpdateStockBody({ row }: { row: GarmentStockRow }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [mode, setMode] = useState<'count' | 'adjust'>('count');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  // One idempotency token per submit context, reused across a failed-then-retry so the server dedupes.
  const tokenRef = useRef<string | null>(null);
  const resetToken = () => {
    tokenRef.current = null;
  };

  const n = Number(qty.trim());
  const validQty =
    qty.trim() !== '' && Number.isInteger(n) && (mode === 'count' ? n >= 0 : n !== 0);
  const nextOnHand = !validQty ? null : mode === 'count' ? n : row.onHand + n;

  const record = useMutation({
    mutationFn: async () => {
      if (!tokenRef.current) tokenRef.current = crypto.randomUUID();
      const token = tokenRef.current;
      const body =
        mode === 'count'
          ? {
              stockKey: row.stockKey,
              countedQty: Math.trunc(n),
              reason: reason.trim() || undefined,
              clientToken: token,
            }
          : {
              stockKey: row.stockKey,
              qtyDelta: Math.trunc(n),
              reason: reason.trim() || undefined,
              clientToken: token,
            };
      const { data, error } =
        mode === 'count'
          ? await client.POST('/garment-stock/count', { body: body as never })
          : await client.POST('/garment-stock/adjust', { body: body as never });
      if (error || !data?.ok) throw new Error(error?.error?.detail ?? 'Could not save.');
      return data.data;
    },
    onSuccess: () => {
      invalidateInventory(qc);
      resetToken();
      setQty('');
      setReason('');
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>Update — {rowBlankLabel(row)}</DialogTitle>
        <DialogDescription>
          <span className="font-medium">Count</span> sets on hand to what you counted on the shelf.{' '}
          <span className="font-medium">Adjust</span> adds or removes a few (damage, found).
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3 rounded-md border p-3">
        <div className="flex items-center gap-1 rounded-md bg-muted p-0.5 text-xs font-medium">
          {(['count', 'adjust'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setMode(m);
                record.reset();
                resetToken();
              }}
              className={
                mode === m
                  ? 'flex-1 rounded bg-background px-2 py-1 shadow-sm'
                  : 'flex-1 rounded px-2 py-1 text-muted-foreground'
              }
            >
              {m === 'count' ? 'Count (set)' : 'Adjust (±)'}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2 text-sm">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">On hand now</Label>
            <div className="tabular-nums">{row.onHand}</div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="stock-qty" className="text-xs text-muted-foreground">
              {mode === 'count' ? 'Set to' : 'Change by (±)'}
            </Label>
            <Input
              id="stock-qty"
              type="number"
              inputMode="numeric"
              step="1"
              min={mode === 'count' ? '0' : undefined}
              className="h-8"
              placeholder={mode === 'count' ? undefined : '+ / -'}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">New on hand</Label>
            <div className="tabular-nums font-medium">{nextOnHand ?? '—'}</div>
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="stock-reason" className="text-xs text-muted-foreground">
            Reason (optional)
          </Label>
          <Input
            id="stock-reason"
            placeholder={mode === 'count' ? 'physical count' : 'e.g. stained / found in a bin'}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={() => record.mutate()} disabled={!validQty || record.isPending}>
            {record.isPending && <Loader2 className="size-4 animate-spin" />}
            {mode === 'count' ? 'Save count' : 'Save adjustment'}
          </Button>
        </div>

        {record.isError && (
          <p className="text-xs text-destructive">
            {record.error instanceof Error ? record.error.message : 'Save failed.'}
          </p>
        )}
        {record.isSuccess && (
          <p className="text-xs text-emerald-700 dark:text-emerald-400">
            Saved — on hand is now {record.data.onHand}.
          </p>
        )}
      </div>
    </>
  );
}

/** The row's ledger history: intakes (scan/manual), counts, adjusts — and, once PR-4 ships, the
 *  PICKs that shipped orders from this shelf, each linking back to its order. */
function StockHistoryBody({ row }: { row: GarmentStockRow }) {
  const client = useApiClient();
  const query = useQuery({
    queryKey: ['garment-stock', 'detail', row.stockKey],
    queryFn: async () => {
      const { data, error } = await client.GET('/garment-stock/movements', {
        params: { query: { key: row.stockKey } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the history.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });
  return (
    <>
      <DialogHeader>
        <DialogTitle>History — {rowBlankLabel(row)}</DialogTitle>
      </DialogHeader>
      {query.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : query.isError ? (
        <p className="text-sm text-destructive">
          {query.error instanceof Error ? query.error.message : 'Failed to load.'}
        </p>
      ) : (
        <MovementList
          rows={query.data.movements}
          emptyText="No history yet — intake a shirt to start one."
        />
      )}
    </>
  );
}

/**
 * The MANUAL intake door — for shelf shirts with no scannable hem tag (older stock). Store →
 * product → variant → quantity; the server resolves the identity through the SAME mapping
 * resolution order matching uses, so this can never mint a key an order would never match.
 */
function AddShirtsBody({
  onClose,
  onPrint,
}: {
  onClose: () => void;
  onPrint: (row: GarmentStockRow) => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [storeId, setStoreId] = useState('');
  const [product, setProduct] = useState<ProductSummary | null>(null);
  const [variantId, setVariantId] = useState('');
  const [qty, setQty] = useState('1');
  const [filter, setFilter] = useState('');
  const tokenRef = useRef<string | null>(null);

  const stores = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const { data, error } = await client.GET('/stores');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load stores.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.items;
    },
  });
  // Mapped products only — an unmapped product can't resolve an identity (the server would 422).
  const products = useQuery({
    queryKey: ['garment-stock', 'add-products', storeId],
    enabled: storeId.length > 0,
    queryFn: async () => {
      const all: ProductSummary[] = [];
      let cursor: string | undefined;
      do {
        const { data, error } = await client.GET('/catalog/{storeId}/products', {
          params: { path: { storeId }, query: { filter: 'mapped', limit: 100, cursor } },
        });
        if (error) throw new Error(error?.error?.detail ?? 'Failed to load products.');
        if (!data?.ok) throw new Error('Empty response from the server.');
        all.push(...data.data.items);
        cursor = data.data.nextCursor ?? undefined;
      } while (cursor);
      return all;
    },
  });
  const detail = useQuery({
    queryKey: ['garment-stock', 'add-variants', storeId, product?.productId ?? ''],
    enabled: Boolean(storeId && product),
    queryFn: async () => {
      const { data, error } = await client.GET('/catalog/{storeId}/products/{productId}', {
        params: { path: { storeId, productId: product?.productId ?? '' } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load variants.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const visibleProducts = useMemo(() => {
    const all = products.data ?? [];
    const f = filter.trim().toLowerCase();
    return f ? all.filter((p) => p.title.toLowerCase().includes(f)) : all;
  }, [products.data, filter]);
  const variants = detail.data?.variants ?? [];
  const chosenVariant = variants.find((v) => v.variantId === variantId);

  const n = Number(qty.trim());
  const validQty = qty.trim() !== '' && Number.isInteger(n) && n >= 1 && n <= 500;

  const add = useMutation({
    mutationFn: async () => {
      if (!storeId || !product || !variantId) throw new Error('Pick a product and size first.');
      if (!tokenRef.current) tokenRef.current = crypto.randomUUID();
      const { data, error } = await client.POST('/garment-stock/intake', {
        body: {
          storeId,
          productId: product.productId,
          variantId,
          quantity: Math.trunc(n),
          clientToken: tokenRef.current,
        },
      });
      if (error || !data?.ok) throw new Error(error?.error?.detail ?? 'Could not add the shirts.');
      return data.data;
    },
    onSuccess: () => {
      invalidateInventory(qc);
      tokenRef.current = null;
      setQty('1');
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add shirts to the shelf</DialogTitle>
        <DialogDescription>
          For finished shirts with no scannable hem tag. Pick the product and size they were made
          for — the shelf tracks the exact printed identity.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3">
        <div className="space-y-1">
          <Label className="text-xs font-medium text-muted-foreground">Store</Label>
          <div className="flex gap-1 rounded-md bg-muted p-0.5 text-xs font-medium">
            {(stores.data ?? []).map((s) => (
              <button
                key={s.storeId}
                type="button"
                onClick={() => {
                  setStoreId(s.storeId);
                  setProduct(null);
                  setVariantId('');
                  add.reset();
                }}
                className={cn(
                  'flex-1 rounded px-2 py-1',
                  storeId === s.storeId ? 'bg-background shadow-sm' : 'text-muted-foreground',
                )}
              >
                {s.brandLabel ?? s.storeId}
              </button>
            ))}
          </div>
        </div>

        {storeId && !product && (
          <div className="space-y-2">
            <Input
              placeholder="Search mapped products…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <div className="max-h-48 overflow-y-auto rounded-md border">
              {products.isPending ? (
                <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" /> Loading products…
                </div>
              ) : visibleProducts.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">No mapped products match.</p>
              ) : (
                visibleProducts.map((p) => (
                  <button
                    type="button"
                    key={p.productId}
                    onClick={() => {
                      setProduct(p);
                      setVariantId('');
                      add.reset();
                    }}
                    className="flex w-full items-center justify-between border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted"
                  >
                    <span className="font-medium">{p.title}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {product && (
          <div className="space-y-3 rounded-md border p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">{product.title}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setProduct(null);
                  setVariantId('');
                  add.reset();
                }}
              >
                Change
              </Button>
            </div>
            <div className="space-y-1">
              <Label htmlFor="stock-variant" className="text-xs text-muted-foreground">
                Colour / size
              </Label>
              {detail.isPending ? (
                <Skeleton className="h-9 w-full" />
              ) : (
                <select
                  id="stock-variant"
                  className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                  value={variantId}
                  onChange={(e) => {
                    setVariantId(e.target.value);
                    add.reset();
                  }}
                >
                  <option value="">Pick a colour + size…</option>
                  {variants.map((v) => (
                    <option key={v.variantId} value={v.variantId}>
                      {[v.color, v.size].filter(Boolean).join(' / ') ||
                        v.variantTitle ||
                        v.variantId}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="flex items-end gap-3">
              <div className="space-y-1">
                <Label htmlFor="stock-add-qty" className="text-xs text-muted-foreground">
                  How many
                </Label>
                <Input
                  id="stock-add-qty"
                  type="number"
                  inputMode="numeric"
                  step="1"
                  min="1"
                  className="h-9 w-24"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                />
              </div>
              <Button
                onClick={() => add.mutate()}
                disabled={!chosenVariant || !validQty || add.isPending}
              >
                {add.isPending && <Loader2 className="size-4 animate-spin" />}
                Add to shelf
              </Button>
            </div>
            {add.isError && (
              <p className="text-xs text-destructive">
                {add.error instanceof Error ? add.error.message : 'Add failed.'}
              </p>
            )}
            {add.isSuccess && (
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-emerald-700 dark:text-emerald-400">
                  Added — {rowBlankLabel(add.data.onHand)} now has {add.data.onHand.onHand} on the
                  shelf.
                </p>
                {/* Label the bag while the shirts are in hand (bin-labels plan § 3.4). */}
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-none"
                  onClick={() => onPrint(add.data.onHand)}
                >
                  <Printer className="size-4" />
                  Print bin label
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onClose}>
          Done
        </Button>
      </div>
    </>
  );
}
